import { api, API } from "@/lib/api";

export async function listLenses() {
  const { data } = await api.get("/lenses");
  return data.items;
}

export async function upsertLens(vertical, lens) {
  const { data } = await api.put(`/lenses/${encodeURIComponent(vertical)}`, lens);
  return data;
}

export async function createCustomLens(lens) {
  const { data } = await api.post("/lenses", lens);
  return data;
}

export async function deleteLens(vertical) {
  const { data } = await api.delete(`/lenses/${encodeURIComponent(vertical)}`);
  return data;
}

export async function generateWalkthrough(analysisId, itemId) {
  const res = await fetch(`${API}/analyses/${analysisId}/build-items/${itemId}/walkthrough`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); msg = j.detail || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

// ---------- Phase 3: sharing + import/export ----------

export async function createLensShare(vertical) {
  const { data } = await api.post(`/lenses/${encodeURIComponent(vertical)}/share`);
  return data; // { share_id, public_url }
}

export async function exportLens(vertical) {
  const { data } = await api.get(`/lenses/${encodeURIComponent(vertical)}/export`);
  return data; // { lens, schema }
}

export async function importLens(payload) {
  const { data } = await api.post(`/lenses/import`, payload);
  return data;
}

export async function readSharedLens(shareId) {
  const { data } = await api.get(`/shared/lens/${encodeURIComponent(shareId)}`);
  return data;
}

export async function adoptSharedLens(shareId, asName) {
  const url = asName
    ? `/shared/lens/${encodeURIComponent(shareId)}/adopt?as=${encodeURIComponent(asName)}`
    : `/shared/lens/${encodeURIComponent(shareId)}/adopt`;
  const { data } = await api.post(url);
  return data;
}

// ---------- Phase 3: walkthrough as code ----------

export async function fetchTestPlanJson(analysisId, itemId) {
  const res = await fetch(`${API}/analyses/${analysisId}/build-items/${itemId}/test-plan.json`);
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); msg = j.detail || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export function testPlanZipUrl(analysisId, itemId) {
  return `${API}/analyses/${analysisId}/build-items/${itemId}/test-plan.zip`;
}

// ---------- Phase 3: Public radar ----------

export async function radarSubmit(target_url) {
  const { data } = await api.post(`/radar`, { target_url });
  return data;
}

export async function radarList() {
  const { data } = await api.get(`/radar`);
  return data.items || [];
}

export async function radarTrending() {
  const { data } = await api.get(`/radar/trending`);
  return data.items || [];
}

// ---------- Phase 4: onboarding-flow walkthrough ----------

export async function generateOnboarding(analysisId) {
  const res = await fetch(`${API}/analyses/${analysisId}/onboarding-walkthrough`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); msg = j.detail || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json(); // { status, error, has_walkthrough }
}

export async function getOnboardingStatus(analysisId) {
  const { data } = await api.get(`/analyses/${analysisId}/onboarding-status`);
  return data; // { status, error, has_walkthrough }
}

// ---------- Live Verify: acceptance criteria vs the live target ----------

export async function verifyBuildItem(analysisId, itemId) {
  const { data } = await api.post(`/analyses/${analysisId}/build-items/${itemId}/verify`);
  return data; // { status, error, verification }
}

export async function getVerification(analysisId, itemId) {
  const { data } = await api.get(`/analyses/${analysisId}/build-items/${itemId}/verify`);
  return data; // { status, error, verification }
}

export async function verifyAll(analysisId) {
  const { data } = await api.post(`/analyses/${analysisId}/verify-all`);
  return data;
}

export async function getVerifyAll(analysisId) {
  const { data } = await api.get(`/analyses/${analysisId}/verify-all`);
  return data; // { status, error, progress, scorecard, item_count }
}

export async function verifyCompetitorAll(analysisId, competitor_url, competitor_name = "") {
  const { data } = await api.post(`/analyses/${analysisId}/verify-competitor`, {
    competitor_url, competitor_name,
  });
  return data;
}

export async function getVerifyCompetitorAll(analysisId) {
  const { data } = await api.get(`/analyses/${analysisId}/verify-competitor`);
  return data; // { status, error, progress, competitor_url, scorecards, target_scorecard }
}

export async function verifyItemCompetitor(analysisId, itemId, competitor_url, competitor_name = "") {
  const { data } = await api.post(
    `/analyses/${analysisId}/build-items/${itemId}/verify-competitor`,
    { competitor_url, competitor_name },
  );
  return data;
}

export async function getItemCompetitorVerification(analysisId, itemId) {
  const { data } = await api.get(`/analyses/${analysisId}/build-items/${itemId}/verify-competitor`);
  return data; // { status, error, progress, verification, competitor_verifications }
}
